/**
 * @typedef {import('./Response').default} Response
 * @typedef {import('../../../../App/shared/OfferService/OfferFields').default} OfferFields
 */

import { offerService } from '../../../../service-container';
import { useEffect, useMemo, useState } from 'react';
import captureError from '../../../../utils/captureError';

/**
 * @param {string} [category]
 * @param {number} [pageNumber]
 * @param {boolean} [isGated]
 * @returns {Response}
 */
export default function useGetAgentVendors(
	category = 'all',
	pageNumber = 1,
	isGated = false
) {
	const [categories, setCategories] = useState(/** @type {string[]} */ ([]));
	const [isGettingData, setIsGettingData] = useState(true);
	const [lastPageNumber, setLastPageNumber] = useState(1);
	const [total, setTotal] = useState(0);
	const [vendors, setVendors] = useState(/** @type {OfferFields[]} */ ([]));

	useEffect(() => {
		if (isGated) {
			setIsGettingData(false);
			return;
		}

		fetchData();
	}, [category, isGated, pageNumber]);

	/** @returns {Promise<void>} */
	async function fetchData() {
		setIsGettingData(true);

		try {
			if (category === 'all') {
				await getTableData();
			} else {
				await getTableDataByCategory();
			}
		} catch (error) {
			captureError(error);
		} finally {
			setIsGettingData(false);
		}
	}

	/** @returns {Promise<void>} */
	async function getTableData() {
		const response = await offerService.getAgentOffers(pageNumber);

		if (response?.categoryList) {
			setCategories(response.categoryList);
		}

		setTotal(response?.total);
		setVendorsData(response);
	}

	/** @returns {Promise<void>} */
	async function getTableDataByCategory() {
		const response = await offerService.getAgentOffersByCategory(
			category,
			pageNumber
		);

		setVendorsData(response);
	}

	/**
	 * @param {{pages:number, offers:OfferFields[]}} response
	 * @returns {void}
	 */
	function setVendorsData(response) {
		if (response?.pages) {
			setLastPageNumber(response.pages);
		}

		setVendors(response.offers);
	}

	return useMemo(
		() => ({
			categories,
			data: vendors,
			isGettingData,
			lastPageNumber,
			total,
		}),
		[categories, vendors, isGettingData, lastPageNumber, total]
	);
}
